import { useState } from 'react';
import MetricCard from '../MetricCard';
import ChartCard from '../charts/ChartCard';
import CustomTooltip from '../charts/CustomTooltip';
import TimePeriodToggle from './TimePeriodToggle';
import { formatNumber } from '../../utils/formatters';
import { groupByTimePeriod } from '../../utils/timePeriod';
import { CHART_COLORS, CHART_THEME } from '../../utils/chartColors';
import {
  FiFileText,
  FiDollarSign,
  FiArrowDownLeft,
  FiPercent,
  FiPlus,
  FiX,
  FiCheck,
  FiRotateCcw
} from 'react-icons/fi';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const REASON_COLORS = {
  requested_by_customer: '#f59e0b',
  duplicate: '#3b82f6',
  fraudulent: '#ef4444',
  unknown: '#94a3b8',
};

const REASON_LABELS = {
  requested_by_customer: 'Customer Request',
  duplicate: 'Duplicate',
  fraudulent: 'Fraudulent',
  unknown: 'Unknown',
};

const EVENT_CONFIG = {
  subscription_new: { icon: FiPlus, bg: 'bg-green-50', text: 'text-green-700', label: 'New Sub' },
  subscription_canceled: { icon: FiX, bg: 'bg-red-50', text: 'text-red-700', label: 'Canceled' },
  invoice_paid: { icon: FiCheck, bg: 'bg-blue-50', text: 'text-blue-700', label: 'Paid' },
  refund: { icon: FiRotateCcw, bg: 'bg-amber-50', text: 'text-amber-700', label: 'Refund' },
};

const ActivityTab = ({ data }) => {
  const [timePeriod, setTimePeriod] = useState('monthly');

  if (!data) return null;

  const { activity, timeSeries } = data;

  const refundsByReason = (activity?.refundsByReason || []).map(r => ({
    ...r,
    name: REASON_LABELS[r.reason] || r.reason,
    color: REASON_COLORS[r.reason] || REASON_COLORS.unknown
  }));

  const recentActivity = activity?.recentActivity || [];
  const totalRefundsByReason = refundsByReason.reduce((sum, r) => sum + r.count, 0);

  // Group timeSeries for refund trend and invoice volume
  const grouped = groupByTimePeriod(timeSeries || [], timePeriod);
  const refundTrend = grouped.map(d => ({
    period: d.period,
    count: d.refundCount,
    amount: d.refunded,
  }));
  const invoiceVolume = grouped.map(d => ({
    period: d.period,
    count: d.invoiceCount,
    amount: d.collected,
  }));

  const isDailyView = timePeriod === 'daily';

  return (
    <div className="space-y-6">
      {/* Metric Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          label="Invoices This Month"
          value={formatNumber(activity.invoicesThisMonth?.count || 0)}
          icon={FiFileText}
        />
        <MetricCard
          label="Revenue This Month"
          value={`$${formatNumber(activity.invoicesThisMonth?.amount || 0)}`}
          icon={FiDollarSign}
        />
        <MetricCard
          label="Refunds This Month"
          value={formatNumber(activity.refundsThisMonth?.count || 0)}
          subtitle={`$${formatNumber(activity.refundsThisMonth?.amount || 0)}`}
          icon={FiArrowDownLeft}
        />
        <MetricCard
          label="Refund Rate"
          value={`${activity.refundRate || 0}%`}
          subtitle="Of all paid invoices"
          icon={FiPercent}
        />
      </div>

      {/* Time Period Toggle */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-gray-700">Activity Trends</h3>
        <TimePeriodToggle value={timePeriod} onChange={setTimePeriod} />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Refund by Reason */}
        <ChartCard title="Refunds by Reason" subtitle="Distribution of refund causes">
          {refundsByReason.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={refundsByReason}
                  dataKey="count"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={55}
                  outerRadius={85}
                  paddingAngle={3}
                  strokeWidth={0}
                >
                  {refundsByReason.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const d = payload[0];
                    return (
                      <div className="bg-slate-800 text-slate-50 px-3 py-2 rounded-lg shadow-lg text-xs">
                        <p className="font-medium mb-1">{d.name}</p>
                        <p className="text-slate-300">{d.value} refunds ({((d.value / totalRefundsByReason) * 100).toFixed(1)}%)</p>
                        <p className="text-slate-300">${d.payload.amount.toLocaleString()}</p>
                      </div>
                    );
                  }}
                />
                <Legend iconType="circle" iconSize={8} />
                <text x="50%" y="47%" textAnchor="middle" className="text-xl font-semibold fill-gray-900">
                  {totalRefundsByReason}
                </text>
                <text x="50%" y="55%" textAnchor="middle" className="text-xs fill-gray-500">
                  Refunds
                </text>
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[280px] flex items-center justify-center text-sm text-gray-400">No refunds recorded</div>
          )}
        </ChartCard>

        {/* Refund Trend */}
        <ChartCard title="Refund Trend" subtitle={`Refund volume — ${timePeriod}`}>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={refundTrend} barSize={isDailyView ? 10 : 24}>
              <CartesianGrid {...CHART_THEME.grid} />
              <XAxis dataKey="period" tick={CHART_THEME.axis} angle={isDailyView ? -45 : 0} textAnchor={isDailyView ? 'end' : 'middle'} height={isDailyView ? 50 : 30} />
              <YAxis yAxisId="left" tick={CHART_THEME.axis} />
              <YAxis yAxisId="right" orientation="right" tick={CHART_THEME.axis} tickFormatter={(v) => `$${v}`} />
              <Tooltip content={<CustomTooltip formatter={(val, name) => name === 'Amount' ? `$${val.toLocaleString()}` : val} />} />
              <Legend iconType="circle" iconSize={8} />
              <Bar yAxisId="left" dataKey="count" fill={CHART_COLORS.danger} name="Count" radius={[3, 3, 0, 0]} opacity={0.7} />
              <Bar yAxisId="right" dataKey="amount" fill={CHART_COLORS.warning} name="Amount" radius={[3, 3, 0, 0]} opacity={0.5} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Invoice Volume */}
        <ChartCard title="Invoice Volume" subtitle={`Paid invoices — ${timePeriod}`} className="lg:col-span-2">
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={invoiceVolume} barSize={isDailyView ? 10 : 20}>
              <CartesianGrid {...CHART_THEME.grid} />
              <XAxis dataKey="period" tick={{ ...CHART_THEME.axis, fontSize: isDailyView ? 9 : 11 }} angle={isDailyView ? -45 : 0} textAnchor={isDailyView ? 'end' : 'middle'} height={isDailyView ? 50 : 30} />
              <YAxis tick={CHART_THEME.axis} />
              <Tooltip content={<CustomTooltip formatter={(val, name) => name === 'Amount' ? `$${val.toLocaleString()}` : val} />} />
              <Legend iconType="circle" iconSize={8} />
              <Bar dataKey="count" fill={CHART_COLORS.info} name="Invoices" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Activity Feed */}
      {recentActivity.length > 0 && (
        <div className="card">
          <h3 className="text-sm font-medium text-gray-700 mb-4">Recent Activity</h3>
          <div className="space-y-0">
            {recentActivity.map((event, i) => {
              const config = EVENT_CONFIG[event.type] || EVENT_CONFIG.invoice_paid;
              const Icon = config.icon;
              const date = new Date(event.timestamp);
              const timeStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) +
                ' ' + date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

              return (
                <div key={i} className="flex items-center gap-3 py-3 border-b border-gray-50 last:border-0">
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full ${config.bg} flex items-center justify-center`}>
                    <Icon className={`w-3.5 h-3.5 ${config.text}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${config.bg} ${config.text}`}>
                        {config.label}
                      </span>
                      <span className="text-sm text-gray-900 truncate">{event.customerEmail}</span>
                    </div>
                    {event.metadata?.invoiceNumber && (
                      <p className="text-xs text-gray-400 mt-0.5">Invoice #{event.metadata.invoiceNumber}</p>
                    )}
                    {event.metadata?.reason && (
                      <p className="text-xs text-gray-400 mt-0.5">Reason: {REASON_LABELS[event.metadata.reason] || event.metadata.reason}</p>
                    )}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className={`text-sm font-medium tabular-nums ${event.type === 'refund' ? 'text-red-600' : 'text-gray-900'}`}>
                      {event.type === 'refund' ? '-' : ''}${formatNumber(event.amount)}
                    </p>
                    <p className="text-xs text-gray-400">{timeStr}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivityTab;
